import React from 'react';

function scalePosition(value, min, max) {
  const top = 48;
  const bottom = 385;
  return bottom - ((Math.min(Math.max(value, min), max) - min) / (max - min)) * (bottom - top);
}

export default function CalciumRuler({ calcium, adjustedCa, albumin, isSI }) {
  const min = isSI ? 1.5 : 6;
  const max = isSI ? 3.5 : 14;
  const low = isSI ? 2.12 : 8.5;
  const high = isSI ? 2.62 : 10.5;
  const unit = isSI ? 'mmol/L' : 'mg/dL';
  const step = isSI ? 0.1 : 0.5;
  const ticks = Array.from(
    { length: Math.round((max - min) / step) + 1 },
    (_, index) => min + index * step,
  );
  const measuredY = calcium === null ? null : scalePosition(calcium, min, max);
  const adjustedY = adjustedCa === null ? null : scalePosition(adjustedCa, min, max);
  const albuminMax = isSI ? 60 : 6;
  const albuminValue = albumin === null ? null : Math.min(Math.max(albumin, 0), albuminMax);
  const albuminPosition = albuminValue === null
    ? null
    : ((albuminMax - albuminValue) / albuminMax) * 100;

  return (
    <aside className="calcium-visual" aria-label="Régua visual de cálcio e albumina">
      <div className="visual-heading">
        <h2>Veja a relação entre cálcio e albumina</h2>
        <p>A faixa sombreada indica a referência ilustrada para o cálcio total.</p>
      </div>
      <div className="visual-chart">
        <div className="ruler-wrap">
          <svg
            className="calcium-svg"
            viewBox="0 0 410 440"
            role="img"
            aria-label={`Régua de cálcio em ${unit}, com faixa de referência e valores medido e estimado`}
          >
            <defs>
              <marker id="displacement-arrow" markerWidth="8" markerHeight="8" refX="5" refY="3" orient="auto">
                <path d="M0,0 L0,6 L6,3 z" className="arrow-head" />
              </marker>
            </defs>
            <rect
              x="100"
              y={scalePosition(high, min, max)}
              width="300"
              height={scalePosition(low, min, max) - scalePosition(high, min, max)}
              rx="4"
              className="reference-band"
            />
            <line x1="100" y1="48" x2="100" y2="385" className="ruler-line" />
            {ticks.map((tick, index) => {
              const y = scalePosition(tick, min, max);
              const wholeTick = Math.abs(tick - Math.round(tick)) < 0.001;
              return (
                <g key={index}>
                  <line
                    x1={wholeTick ? 87 : 92}
                    x2="111"
                    y1={y}
                    y2={y}
                    className={wholeTick ? 'ruler-tick ruler-tick-major' : 'ruler-tick'}
                  />
                  {wholeTick && (
                    <text x="72" y={y + 5} className="ruler-label" textAnchor="end">
                      {tick.toFixed(isSI ? 1 : 0)}
                    </text>
                  )}
                </g>
              );
            })}
            <text x="100" y="421" className="svg-caption" textAnchor="middle">{unit}</text>
            <text x="394" y={scalePosition(high, min, max) + 16} className="reference-label" textAnchor="end">
              faixa de referência · {low.toLocaleString('pt-BR')}–{high.toLocaleString('pt-BR')} {unit}
            </text>
            {measuredY !== null && (
              <g className="marker-measured">
                <line x1="100" x2="142" y1={measuredY} y2={measuredY} className="marker-line" />
                <circle cx="100" cy={measuredY} r="8" className="marker-dot" />
                <text x="153" y={measuredY - 7} className="marker-label">
                  medido
                </text>
                <text x="153" y={measuredY + 10} className="marker-value">
                  {calcium.toFixed(2)} {unit}
                </text>
              </g>
            )}
            {adjustedY !== null && (
              <g className="marker-adjusted">
                <line x1="100" x2="142" y1={adjustedY} y2={adjustedY} className="marker-line" />
                <circle cx="100" cy={adjustedY} r="8" className="marker-dot" />
                <text x="153" y={adjustedY + 17} className="marker-label">
                  corrigido*
                </text>
                <text x="153" y={adjustedY + 34} className="marker-value">
                  {adjustedCa.toFixed(2)} {unit}
                </text>
              </g>
            )}
            {measuredY !== null && adjustedY !== null && Math.abs(measuredY - adjustedY) > 4 && (
              <line
                x1="132"
                x2="132"
                y1={measuredY}
                y2={adjustedY}
                className="displacement-arrow"
                markerEnd="url(#displacement-arrow)"
              />
            )}
          </svg>
        </div>
        <div className="albumin-scale" aria-label={`Albumina: ${albuminValue === null ? 'não informada' : albuminValue.toFixed(1) + (isSI ? ' g/L' : ' g/dL')}`}>
          <span className="albumin-title">Albumina</span>
          <div className="albumin-track">
            <span className="albumin-reference" />
            {albuminPosition !== null && (
              <span className="albumin-marker" style={{ top: `${albuminPosition}%` }} />
            )}
          </div>
          <span className="albumin-value">{albuminValue === null ? '—' : albuminValue.toFixed(1)}</span>
          <span className="albumin-unit">{isSI ? 'g/L' : 'g/dL'}</span>
        </div>
      </div>
      <div className="visual-legend">
        <span><i className="legend-dot measured-dot" />Cálcio total informado</span>
        <span><i className="legend-dot adjusted-dot" />Estimativa ajustada</span>
      </div>
    </aside>
  );
}
